import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHeader from '../components/PageHeader';
import CompanyLogos from '../components/CompanyLogos';
import { examParts, courseLearnings, team, stats } from '../mock';
import {
  ArrowUpRight, Check, Download, Star, Clock, BookOpen, Users,
  Award, Briefcase, GraduationCap, TrendingUp, Calendar, Globe2, PlayCircle, Quote,
} from 'lucide-react';
import { SectionHeader } from '../components/Services';

const timeline = [
  { label: 'Month 1', title: 'Foundation & Part 1 kickoff', points: ['US tax fundamentals', 'IRC structure & terminology', 'Form 1040 walkthrough', 'Live filing lab'] },
  { label: 'Month 2', title: 'Individuals deep dive', points: ['Filing status & dependents', 'Deductions & credits', 'Retirement & investments', 'Practice test #1'] },
  { label: 'Month 3', title: 'SEE Part 1 exam window', points: ['Prometric slot booked', 'Mock exam simulation', '1-on-1 mentor sessions', 'PART 1 exam attempt'] },
  { label: 'Month 4', title: 'Businesses (Part 2) fast-track', points: ['C-Corp / S-Corp / LLC', 'Form 1120 & 1065', 'Depreciation & inventory', 'Payroll & self-employment'] },
  { label: 'Month 5', title: 'Representation (Part 3)', points: ['Practice before the IRS', 'Circular 230 ethics', 'Audits, appeals, collections', 'Client communication'] },
  { label: 'Month 6', title: 'Placement & licensing', points: ['Form 23 submission', 'Big 14 mock interviews', 'Resume & LinkedIn workshop', 'Get placed'] },
];

const outcomes = [
  { label: 'Global recognition', icon: Globe2, desc: 'EA is the only US federal-level tax credential — accepted in all 50 states.' },
  { label: 'Highest tax authority', icon: Award, desc: 'Unlimited practice rights before the IRS, same as CPAs and attorneys.' },
  { label: 'Big 14 placement', icon: Briefcase, desc: 'Direct pipeline into EY, Deloitte, PwC, KPMG, BDO, RSM, Grant Thornton and more.' },
  { label: 'No degree required', icon: GraduationCap, desc: 'Only the SEE exam matters. Commerce, arts, science — anyone with grit qualifies.' },
  { label: 'Remote-first career', icon: TrendingUp, desc: 'Work from India for US firms. Salaries from ₹8 LPA freshers to ₹30 LPA seniors.' },
  { label: 'Lifetime license', icon: Star, desc: 'No annual renewal. Just 72 CE hours every 3 years to stay current.' },
];

const testimonials = [
  {
    quote: 'Cleared all 3 parts in 5 months. The mock tests were harder than the real SEE — that’s exactly what prepared me.',
    name: 'Anjali Reddy',
    role: 'EA · Senior Tax Associate at Withum',
    image: '/images/site/01-pexels-photo-8297076.jpg',
  },
  {
    quote: 'The placement team put me in front of 4 Big 14 recruiters in one week. I signed with BDO before I even took Part 3.',
    name: 'Rahul Menon',
    role: 'EA candidate · Placed at BDO India',
    image: '/images/site/36-photo-1507003211169-0a1dd7228f2d.jpg',
  },
  {
    quote: 'I run my own US tax practice now serving 40+ NRIs. Eduvantage taught me the tax, then taught me the business.',
    name: 'Priya Iyer',
    role: 'EA · Founder, Iyer Tax LLC',
    image: '/images/site/12-photo-1573496359142-b8d87734a5a2.jpg?w=600',
  },
];

const faqs = [
  { q: 'Do I need a US SSN to become an EA?', a: 'No. You can apply for an ITIN through us if you don’t have an SSN. The SEE exam and PTIN registration are open to Indian residents.' },
  { q: 'How much does the SEE exam cost?', a: 'Each of the three parts costs $259 USD payable to Prometric. Total exam cost ~$777. Course fees are separate.' },
  { q: 'What is the pass rate for the SEE?', a: 'The IRS-published pass rate is roughly 70% for Part 1, 62% for Part 2, and 84% for Part 3. Our student pass rate is 96% first-attempt.' },
  { q: 'Can I complete this while working full-time?', a: 'Yes. Live batches run 7–9pm IST on weekdays and 10am–1pm on Saturdays. Every session is recorded.' },
  { q: 'Is there a placement guarantee?', a: 'Yes. We guarantee a minimum of 6 interview opportunities with Big 14 or top mid-market US CPA firms within 60 days of clearing Part 3.' },
  { q: 'What if I fail a part?', a: 'You can retake it within a 3-year window. Eduvantage provides free retest coaching sessions if you don’t clear a part first attempt.' },
];

const bigNumbers = [
  { n: '96%', l: 'first-attempt pass rate' },
  { n: '?12 LPA', l: 'avg fresher package' },
  { n: '2,488+', l: 'students trained' },
  { n: '14/14', l: 'Big 14 firms hiring alumni' },
];

const EACourse = () => {
  return (
    <div className="font-body bg-paper text-[#1c1c1c]">
      <Navbar />
      <PageHeader
        eyebrow="Flagship Program"
        title={<>Enrolled Agent <em className="italic">Certification</em></>}
        description="Become a federally-authorized US tax practitioner in 6 months. 3 SEE exams, live training, and guaranteed Big 14 placement."
        breadcrumb="EA Course"
      />

      {/* Course overview */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 card-soft rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-4 mb-6">
                <img src={team[0].image} alt={team[0].name} className="w-14 h-14 rounded-full object-cover" />
                <div>
                  <p className="text-[13px] text-[#3d3d3d] font-body">Instructor</p>
                  <p className="font-body font-semibold text-[#143324]">{team[0].name}</p>
                </div>
              </div>
              <h2 className="font-serif-display text-[#143324] text-3xl md:text-4xl mb-4">Course description</h2>
              <p className="text-[#3d3d3d] text-[15px] leading-relaxed font-body mb-6">
                An Enrolled Agent (EA) is a federally licensed tax professional authorized by the IRS to represent taxpayers in the U.S. To become an EA, you must pass the IRS Special Enrollment Examination (SEE) — a three-part test covering individual and business taxes, plus IRS procedures and ethics. No college degree is required — only exam success, PTIN registration, and a background check. EAs enjoy strong prospects in tax firms, accounting, and freelance roles.
              </p>

              <h3 className="font-body font-semibold text-[#143324] text-lg mb-4 mt-8">What you’ll learn</h3>
              <ul className="space-y-3">
                {courseLearnings.map((c, i) => (
                  <li key={i} className="flex items-start gap-3 text-[14px] text-[#1c1c1c] font-body">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#143324] text-[#f5f1e6] flex items-center justify-center shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right sidebar */}
            <div className="space-y-4">
              <div className="card-soft rounded-2xl p-6">
                <div className="space-y-4 text-[14px] font-body">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-[#3d3d3d]"><Clock size={15} /> Duration</span>
                    <span className="text-[#143324] font-medium">6 Months</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-[#3d3d3d]"><BookOpen size={15} /> Modules</span>
                    <span className="text-[#143324] font-medium">3 Parts (SEE)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-[#3d3d3d]"><Users size={15} /> Batch Size</span>
                    <span className="text-[#143324] font-medium">15–20 students</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-[#3d3d3d]"><Star size={15} className="star-filled" fill="currentColor" /> Rating</span>
                    <span className="text-[#143324] font-medium">4.9 / 5</span>
                  </div>
                </div>
                <Link to="/contact" className="mt-6 w-full inline-flex items-center justify-center gap-2 btn-primary px-5 py-3 rounded-lg text-[14px] font-body">
                  Enroll Now <ArrowUpRight size={15} />
                </Link>
                <p className="mt-3 text-center text-[12px] text-[#3d3d3d] font-body">EMI options available</p>
              </div>

              <div className="card-soft rounded-2xl p-6">
                <h4 className="font-body font-semibold text-[#143324] mb-3">Career Opportunities</h4>
                <ul className="space-y-2 text-[13px] text-[#3d3d3d] font-body">
                  <li>{'•'} Tax Advisor</li>
                  <li>{'•'} Tax Consultant</li>
                  <li>{'•'} Tax Auditor</li>
                  <li>{'•'} Independent Practitioner</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exam Parts */}
      <section className="py-16 md:py-20 bg-paper-soft relative">
        <div className="squiggle-bg" />
        <div className="max-w-6xl mx-auto px-6 relative">
          <SectionHeader
            eyebrow="SEE Exam Structure"
            title={<>The exam is divided into <em className="italic">three parts</em></>}
            description="Each part is a 4-hour objective test with 100 MCQs. Score 105/130 to pass and get instant results."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {examParts.map((p, i) => (
              <div key={i} className="card-soft rounded-2xl p-6 md:p-7">
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="font-serif-display text-[#143324] italic text-2xl">EA</span>
                  <span className="text-xs uppercase tracking-widest text-[#3d3d3d] font-body">{p.part}</span>
                </div>
                <h3 className="font-body font-semibold text-[#143324] text-xl md:text-2xl mb-4">{p.title}</h3>
                <ul className="space-y-2 mb-6">
                  {p.topics.map((t, j) => (
                    <li key={j} className="flex items-start gap-2 text-[13px] text-[#3d3d3d] font-body">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#143324] shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
                <button className="inline-flex items-center gap-2 text-[13px] text-[#143324] font-body hover:gap-3 transition-all">
                  <Download size={14} /> Download Brochure
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6-month Timeline */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            eyebrow="Your 6-Month Roadmap"
            title={<>From day one to <em className="italic">enrolled agent</em></>}
            description="Every month has a defined milestone. Every week has a live class, a lab, and a mock test. Zero guesswork."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {timeline.map((t, i) => (
              <div key={i} className="card-soft rounded-2xl p-6 md:p-7 relative">
                <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#143324] text-[#f5f1e6] text-[11px] tracking-wider uppercase font-body">
                  <Calendar size={11} /> {t.label}
                </div>
                <h3 className="font-body font-semibold text-[#143324] text-lg md:text-xl mt-3 mb-4">{t.title}</h3>
                <ul className="space-y-2">
                  {t.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2 text-[13px] text-[#3d3d3d] font-body">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#143324] shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why EA — Outcomes */}
      <section className="py-16 md:py-24 bg-paper-soft relative">
        <div className="squiggle-bg" />
        <div className="max-w-6xl mx-auto px-6 relative">
          <SectionHeader
            eyebrow="Why Choose EA"
            title={<>The <em className="italic">most credible</em> US tax license for Indians</>}
            description="EA opens doors that CA, CMA and MBA simply can’t — direct US federal recognition, no degree gatekeeping, and a booming outsourcing market."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {outcomes.map((o, i) => {
              const Icon = o.icon;
              return (
                <div key={i} className="card-soft rounded-2xl p-6 md:p-7">
                  <div className="w-11 h-11 rounded-xl bg-[#143324] text-[#f5f1e6] flex items-center justify-center mb-4">
                    <Icon size={18} strokeWidth={1.7} />
                  </div>
                  <h3 className="font-body font-semibold text-[#143324] text-lg mb-2">{o.label}</h3>
                  <p className="text-[13px] text-[#3d3d3d] leading-relaxed font-body">{o.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Big Numbers */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="card-soft rounded-3xl p-8 md:p-14 bg-[#f5f1e6] text-[#143324] relative overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {bigNumbers.map((b, i) => (
                <div key={i}>
                  <div className="font-serif-display text-[#143324] text-4xl md:text-6xl leading-none mb-2">{b.n}</div>
                  <p className="text-[13px] text-[#3d3d3d] font-body">{b.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Instructor Spotlight */}
      <section className="py-16 md:py-24 bg-paper-soft">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            eyebrow="Instructor Spotlight"
            title={<>Learn from <em className="italic">practicing EAs</em>, not textbook readers</>}
            description="Every module is taught by an active US-licensed Enrolled Agent with a client portfolio in the current filing season."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {team.map((t, i) => (
              <div key={i} className="card-soft rounded-2xl overflow-hidden">
                <img src={t.image} alt={t.name} className="w-full aspect-[4/3] object-cover" />
                <div className="p-6">
                  <h4 className="font-body font-semibold text-[#143324] text-lg mb-1">{t.name}</h4>
                  <p className="text-[12px] tracking-wider uppercase text-[#3d3d3d] font-body mb-3">{t.role}</p>
                  <p className="text-[13px] text-[#3d3d3d] font-body leading-relaxed">
                    {t.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            eyebrow="Student Stories"
            title={<>From <em className="italic">first exam</em> to first paycheck</>}
            description="Real names. Real firms. Real career pivots. Every EA started somewhere — hear where they came from."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="card-soft rounded-2xl p-6 md:p-7 flex flex-col">
                <Quote size={22} className="text-[#143324] mb-4" strokeWidth={1.7} />
                <p className="text-[14px] text-[#1c1c1c] leading-relaxed font-body mb-6 flex-1">{t.quote}</p>
                <div className="flex items-center gap-3 pt-4 border-t border-[#143324]/10">
                  <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="font-body font-semibold text-[#143324] text-[14px]">{t.name}</p>
                    <p className="text-[12px] text-[#3d3d3d] font-body">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility + Pattern + Stats */}
      <section className="py-16 md:py-20 bg-paper-soft">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          <div className="card-soft rounded-2xl p-6 md:p-7">
            <h4 className="font-body font-semibold text-[#143324] text-lg mb-4">Eligibility Check</h4>
            <p className="text-[13px] font-body text-[#3d3d3d] mb-2 font-medium">Exam:</p>
            <ul className="space-y-1.5 text-[13px] text-[#3d3d3d] font-body mb-4">
              <li>{'•'} Candidates must be at least 18 years old</li>
              <li>{'•'} Candidates must obtain a PTIN to sit for SEE Exams</li>
            </ul>
            <p className="text-[13px] font-body text-[#3d3d3d] mb-2 font-medium">Certificate:</p>
            <ul className="space-y-1.5 text-[13px] text-[#3d3d3d] font-body">
              <li>{'•'} Pass all 3 papers of the SEE Exam</li>
              <li>{'•'} Complete Form 23 for licensing application</li>
            </ul>
          </div>
          <div className="card-soft rounded-2xl p-6 md:p-7">
            <h4 className="font-body font-semibold text-[#143324] text-lg mb-4">Pattern & Scoring</h4>
            <ul className="space-y-1.5 text-[13px] text-[#3d3d3d] font-body">
              <li>{'•'} 3 Parts — Flexible exam dates till 28th Feb 2026</li>
              <li>{'•'} 100 MCQ questions per paper</li>
              <li>{'•'} Each question carries 1.3 marks</li>
              <li>{'•'} 4 hours per exam (50Q ? 15min break ? 50Q)</li>
              <li>{'•'} Score 105/130 to pass</li>
              <li>{'•'} Instant results after exam</li>
            </ul>
          </div>
          <div className="card-soft rounded-2xl p-6 md:p-7">
            <h4 className="font-body font-semibold text-[#143324] text-lg mb-4">Course Stats</h4>
            <div className="space-y-4">
              {stats.map((s, i) => (
                <div key={i}>
                  <div className="font-serif-display text-[#143324] text-3xl leading-none">{s.value}</div>
                  <p className="text-[13px] text-[#3d3d3d] font-body mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeader
            eyebrow="Frequently Asked"
            title={<>Questions we hear <em className="italic">every week</em></>}
            description="From SSN requirements to placement guarantees — straight answers, no jargon."
          />
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="card-soft rounded-2xl px-6 py-5 group" data-testid={`ea-faq-${i}`}>
                <summary className="cursor-pointer font-body font-semibold text-[#143324] text-[15px] flex items-center justify-between list-none">
                  <span>{f.q}</span>
                  <span className="ml-3 w-6 h-6 rounded-full bg-[#f5f1e6] border border-[#143324]/15 flex items-center justify-center text-[#143324] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[13.5px] text-[#3d3d3d] leading-relaxed font-body">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-paper-soft">
        <div className="max-w-5xl mx-auto px-6">
          <div className="card-soft rounded-3xl p-8 md:p-14 bg-[#143324] text-[#f5f1e6] relative overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#143324]/15 bg-white/60 text-[11px] tracking-[0.2em] uppercase text-[#143324] font-body mb-4">
                  <PlayCircle size={12} /> Live batch · starts every 15 days
                </div>
                <h3 className="font-serif-display text-[#143324] text-4xl md:text-5xl leading-tight mb-4">
                  Your next Big 14 offer letter <em className="italic">starts here</em>.
                </h3>
                <p className="text-[#3d3d3d] font-body leading-relaxed mb-6 max-w-md">
                  Book a free 45-min trial class, sit in on a live SEE prep session, and meet the instructors before you enroll.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-3">
                <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-[14px] font-body">
                  Enroll now <ArrowUpRight size={15} />
                </Link>
                <a href="tel:+917010492161" className="btn-outline inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-[14px] font-body">
                  Call a counselor
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CompanyLogos />
      <Footer />
    </div>
  );
};

export default EACourse;
